import React, { CSSProperties } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View, ViewProps } from "react-native";
import firebase from "firebase/app";
import { useFocusEffect, useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { kColors } from "src/kColors";
import { PdFormLabel } from "src/components/form-control";
import { PdBadge, PdBadgeLabel, PdStack, PdText, tPdText } from "src/components/layout";
import { useIsMobile } from "src/hooks/ui";
import {
  PdHookFormErrorMessage,
  PdHookFormImagePicker,
  PdHookFormInput,
  PdHookFormInputFloat,
  PdHookFormInputPdf,
  PdHookFormInputPhone,
  PdHookFormSelect,
  PdHookFormSwitch,
} from "src/components/hook-form";
import { kStyleConst } from "src/kStyle";
import { retailerCategoryOptions } from "src/util/comboBoxData";
import { getDefaultLongAddress } from "src/redux/company/defaultData";
import { formatDate, formatMoney, formatPhoneNumber, formatShortAddress } from "src/util/gFormatting";
import { getDefaultAddress } from "src/util/user";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useChangeEmailModal, useRetailerAddressesModal } from "src/hooks/modals";
import { PdDivider } from "src/components/layout/table";
import { kFonts } from "src/kFonts";
import { releaseRetailerCredit, updateRetailer } from "src/redux/AdminSettings/actions";

import { tBoutique, tRetailerCategory, tUser } from "tmt_api/src/types/types.validator";
import { tImagePickerValue } from "src/components/forms/picker";
import { useAppDispatch } from "src/redux";
import { activateRetailer, deactivateRetailer } from "src/redux/user/actions";
import { capitalize } from "lodash";
import { kRoute } from "src/kRoute";
import { setCurrentThread } from "src/redux/messages/actions";
import { getRetailerSettings } from "src/util/apiNew";
import { useFocusableQuery } from "src/hooks/api";
import { PdInput, tPdInputPdfValue } from "src/components/forms/input";
import { documentRetailer } from "src/db/retailer.db";
import { queryUser } from "src/db/user.db";
import { getSafeUrl } from "src/util/functions";

type tAdminRetailerSettingsParams = {
  params: { retailerId?: string };
  key: string;
  name: string;
};

type tAdminRetailerFormData = {
  name: string;
  contactPerson: string;
  website: string;
  instagram: string;
  facebook: string;
  storeType: { label: string; value: tRetailerCategory } | null;
  aboutMe: string;
  taxInfo: tPdInputPdfValue;
  taxId: string;
  userName: string;
  userTitle: string;
  availableMarketplaceCreditAmount: string;
  shouldReleaseCredit: boolean;
  phone: string;
  accountManagerName: string;
  internalRank: string;
  internalPrimaryCategory: { label: string; value: tRetailerCategory } | null;
  internalSecondaryCategory: { label: string; value: tRetailerCategory } | null;
  internalTertiaryCategory: { label: string; value: tRetailerCategory } | null;
  internalNotes: string;
  avatar: tImagePickerValue | null;
};

const orderedCategories = retailerCategoryOptions.sort((c1, c2) => c1.label.localeCompare(c2.label));

const getThemeColorFromQualificationStatus = (status?: "incomplete" | "approved" | "declined"): string => {
  if (status === "declined") return kColors.red200;
  if (status === "incomplete") return kColors.yellow200;
  if (status === "approved") return kColors.orange200;
  return kColors.gray300;
};

const PdSectionBadge: React.FC<ViewProps> = ({ style, ...rest }) => {
  return <View style={[styles.pdSectionBadge, style]} {...rest} />;
};

const PdSectionBadgeLabel: React.FC<tPdText> = ({ style, ...rest }) => {
  return <PdText style={[styles.pdSectionBadgeLabel, style]} {...rest} />;
};

const AdminRetailerForm: React.FC<{ data: { retailer: tBoutique; user: tUser } }> = ({ data }) => {
  const [isActivatingOrDeactivating, setActivatingOrDeactivating] = React.useState(false);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const kNav = useNavigation();
  const dispatch = useAppDispatch();
  const rMessagesThreads = useSelector((state) => state.MessageThreadsReducer.rThreads);
  const isMobile = useIsMobile();
  const retailerAddressesModal = useRetailerAddressesModal();
  const changeEmailModal = useChangeEmailModal();
  const query = useFocusableQuery(React.useCallback(() => getRetailerSettings(data.retailer.id), [data.retailer.id]));
  const formMethods = useForm<tAdminRetailerFormData>({
    defaultValues: {
      name: data.retailer?.name || "",
      contactPerson: data.retailer?.contactPerson || "",
      website: data.retailer?.website || "",
      instagram: data.retailer?.links.instagram ?? "",
      facebook: data.retailer?.links.facebook ?? "",
      storeType: orderedCategories.find((category) => category.value === data.retailer?.primaryCategory) || null,
      aboutMe: data.retailer?.aboutMe || "",
      taxInfo: data.retailer?.taxInfo ? { uri: data.retailer.taxInfo.url, name: data.retailer.taxInfo.name } : null,
      taxId: data.retailer?.taxId || "",
      userName: data.user?.userName || "",
      userTitle: data.user?.userTitle || "",
      phone: data.user?.phone || "",
      availableMarketplaceCreditAmount: "",
      shouldReleaseCredit: false,
      accountManagerName: data.retailer?.accountManagerName ?? "",
      internalRank: data.retailer?.internalRank ?? "",
      internalPrimaryCategory:
        orderedCategories.find((category) => category.value === data.retailer?.internalPrimaryCategory) || null,
      internalSecondaryCategory:
        orderedCategories.find((category) => category.value === data.retailer?.internalSecondaryCategory) || null,
      internalTertiaryCategory:
        orderedCategories.find((category) => category.value === data.retailer?.internalTertiaryCategory) || null,
      internalNotes: data.retailer?.internalNotes ?? "",
      avatar: data.retailer?.avatar ? { uri: data.retailer.avatar } : null,
    },
  });

  const address = data.retailer?.addresses ? getDefaultAddress(data.retailer?.addresses) : getDefaultLongAddress();

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    setSubmitting(true);
    const availableMarketplaceCreditAmount = formData.availableMarketplaceCreditAmount
      ? parseFloat(formData.availableMarketplaceCreditAmount)
      : data.retailer.availableMarketplaceCreditAmount;
    const lifetimeMarketplaceCreditAmount = data.retailer.lifetimeMarketplaceCreditAmount ?? 0;
    const retailer: tBoutique = {
      ...data.retailer,
      name: formData.name,
      taxId: formData.taxId,
      website: formData.website,
      contactPerson: formData.contactPerson,
      primaryCategory: formData.storeType?.value,
      aboutMe: formData.aboutMe,
      links: { instagram: getSafeUrl(formData.instagram), facebook: getSafeUrl(formData.facebook) },
      phone: formData.phone,
      availableMarketplaceCreditAmount,
      lifetimeMarketplaceCreditAmount: Math.max(
        (availableMarketplaceCreditAmount || 0) -
          (data.retailer.availableMarketplaceCreditAmount ?? 0) +
          lifetimeMarketplaceCreditAmount,
        lifetimeMarketplaceCreditAmount,
      ),
      accountManagerName: formData.accountManagerName,
      internalRank: formData.internalRank,
      internalNotes: formData.internalNotes,
      internalPrimaryCategory: formData.internalPrimaryCategory?.value,
      internalSecondaryCategory: formData.internalSecondaryCategory?.value,
      internalTertiaryCategory: formData.internalTertiaryCategory?.value,
    };
    if (formData.taxInfo?.file) {
      const snapshot = await firebase
        .storage()
        .ref(`retailer/${data.retailer.id}/uploads/tax_info.pdf`)
        .put(formData.taxInfo.file);
      const url: string = await snapshot.ref.getDownloadURL();
      retailer.taxInfo = { name: formData.taxInfo?.name ?? "", url };
    }
    if (formData.avatar?.file) {
      const fileExtension = formData.avatar.file.type.replace(/^.*\//gm, "");
      const snapshot = await firebase
        .storage()
        .ref(`retailer/${data.retailer.id}/uploads/profile.${fileExtension}`)
        .put(formData.avatar.file);
      retailer.avatar = await snapshot.ref.getDownloadURL();
    }

    if (formData.shouldReleaseCredit) {
      await dispatch(releaseRetailerCredit(data.retailer.id));
    }

    const contactNameParts = formData.contactPerson.split(/(\s+)/);
    const firstName = contactNameParts.shift() || data.user.firstName;
    const lastName = contactNameParts.join("").trim();

    const user: tUser = {
      ...data.user,
      firstName,
      lastName,
      userName: formData.userName,
      userTitle: formData.userTitle,
      phone: formData.phone,
    };

    await dispatch(updateRetailer(retailer, user));

    formMethods.setValue("availableMarketplaceCreditAmount", "");
    formMethods.setValue("shouldReleaseCredit", false);

    setSubmitting(false);
  });

  const handleOnPressChangeEmail = () => {
    changeEmailModal.openAsync({ accountType: "retailer", companyId: data.retailer.id });
  };

  const handleOnPressManageAddresses = () => {
    retailerAddressesModal.openAsync({ retailerId: data.retailer.id });
  };

  const handleOnPressMessageRetailer = () => {
    try {
      const rThreadId = rMessagesThreads.find((thread) => {
        const kPatternMatches = thread.recipient.match(/\/(\w|\d|-)+/);
        return data.retailer.id === kPatternMatches?.[0]?.replace("/", "");
      });
      if (!rThreadId) throw new Error("An error occurred while attempting to parse the recipient's id.");

      dispatch(setCurrentThread(rThreadId));
      kNav.navigate(kRoute.P04);
    } catch (er) {
      dispatch({
        type: "RIBBON",
        message: new Error("Please use the left menu to send a message to the Retailer"),
      });
    }
  };

  const handleOnPressActivate = async () => {
    setActivatingOrDeactivating(true);
    await dispatch(activateRetailer(data.retailer.id));
    setActivatingOrDeactivating(false);
  };

  const handleOnPressDeactivate = async () => {
    setActivatingOrDeactivating(true);
    await dispatch(deactivateRetailer(data.retailer.id));
    setActivatingOrDeactivating(false);
  };

  return (
    <FormProvider {...formMethods}>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[1, 1]}
        gap={3}
        style={[styles.widthFull, styles.mb3]}
      >
        <View style={[styles.bgWhite, styles.px5, styles.py5]}>
          <View style={[styles.flexRow, styles.alignCenter]}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>1</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              The Basics
            </PdText>
          </View>
          <PdDivider style={[styles.mt1, styles.mb4]} />
          <View style={styles.mb3}>
            <PdFormLabel>Company Name *</PdFormLabel>
            <PdHookFormInput name="name" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="name" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Contact Full Name *</PdFormLabel>
            <PdHookFormInput name="contactPerson" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="contactPerson" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Phone Number *</PdFormLabel>
            <PdHookFormInputPhone
              name="phone"
              placeholder="(XXX) XXX-XXXX"
              autoCompleteType="tel"
              rules={{ required: "Required" }}
              onSubmitEditing={handleOnSubmit}
            />
            <PdHookFormErrorMessage name="phone" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Email *</PdFormLabel>
            <PdInput isDisabled value={data.user.email} />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Resale Certificate Copy</PdFormLabel>
            <PdHookFormInputPdf isDisabled={data.user.isDeactivated} name="taxInfo" />
            <PdHookFormErrorMessage name="taxInfo" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Company Tax ID</PdFormLabel>
            <PdHookFormInput name="taxId" />
            <PdHookFormErrorMessage name="taxId" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary & Default Ship to Address</PdFormLabel>
            {!address.name && !address.address1 ? (
              <PdText>-</PdText>
            ) : (
              <>
                {!!address.name && <PdText>{address.name}</PdText>}
                <PdText>{address.address1}</PdText>
                {!!address.address2 && <PdText>{address.address2}</PdText>}
                <PdText>{formatShortAddress(address)}</PdText>
                {!!address.phone && <PdText>{formatPhoneNumber(address.phone)}</PdText>}
              </>
            )}
          </View>
          <PdButton
            size="sm"
            colorScheme="pink"
            onPress={handleOnPressManageAddresses}
            isDisabled={data.user.isDeactivated}
            style={styles.mrAuto}
          >
            <PdButtonLabel>Manage Addresses</PdButtonLabel>
          </PdButton>
        </View>

        <View style={[styles.bgWhite, styles.px5, styles.py5]}>
          <View style={[styles.flexRow, styles.alignCenter]}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>2</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              External
            </PdText>
          </View>
          <PdDivider style={[styles.mt1, styles.mb4]} />
          <View style={styles.mb3}>
            <PdFormLabel>Website *</PdFormLabel>
            <PdHookFormInput name="website" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="website" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Instagram</PdFormLabel>
            <PdHookFormInput name="instagram" onSubmitEditing={handleOnSubmit} />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Facebook</PdFormLabel>
            <PdHookFormInput name="facebook" onSubmitEditing={handleOnSubmit} />
          </View>
          <View style={[styles.mb3, styles.zIndex1]}>
            <PdFormLabel>Store Type *</PdFormLabel>
            <PdHookFormSelect
              name="storeType"
              options={orderedCategories}
              rules={{ required: "Required" }}
              isClearable={false}
            />
            <PdHookFormErrorMessage name="storeType" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>About Me</PdFormLabel>
            <PdHookFormInput
              name="aboutMe"
              multiline
              numberOfLines={8}
              placeholder="Give a brief bio about your business."
            />
            <PdHookFormErrorMessage name="aboutMe" />
          </View>
          <View>
            <PdFormLabel>Profile Image</PdFormLabel>
            <PdHookFormImagePicker
              isDisabled={data.user.isDeactivated}
              name="avatar"
              style={[
                styles.sizeLg,
                styles.roundFull,
                styles.bgGray,
                styles.alignCenter,
                styles.justifyCenter,
                styles.mt1,
              ]}
              mobileStyle={[styles.sizeLg, styles.roundFull]}
              webStyle={{ ...webStyles.sizeLg, ...webStyles.roundFull }}
            />
            <PdHookFormErrorMessage name="avatar" />
          </View>
        </View>
      </PdStack>

      <View style={[styles.bgWhite, styles.px5, styles.py5]}>
        <View style={[styles.flexRow, styles.alignCenter]}>
          <PdSectionBadge>
            <PdSectionBadgeLabel>3</PdSectionBadgeLabel>
          </PdSectionBadge>
          <PdText variant="fieldset" style={styles.ml2}>
            Admin
          </PdText>
        </View>
        <PdDivider style={[styles.mt1, styles.mb4]} />
        <PdStack direction={isMobile ? "vertical" : "horizontal"} gap={5} style={styles.widthFull}>
          <View style={!isMobile ? styles.flex1 : {}}>
            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3]}
            >
              <View>
                <PdFormLabel>Available in Terms</PdFormLabel>
                <PdText>{data.retailer?.availableBalance ? formatMoney(data.retailer?.availableBalance) : "-"}</PdText>
              </View>
              <View>
                <PdFormLabel>NET Terms Status</PdFormLabel>
                {data.retailer?.qualificationStatusForTerms ? (
                  <PdBadge
                    style={[
                      {
                        backgroundColor: getThemeColorFromQualificationStatus(
                          data.retailer?.qualificationStatusForTerms,
                        ),
                      },
                      styles.mrAuto,
                    ]}
                  >
                    <PdBadgeLabel>{capitalize(data.retailer?.qualificationStatusForTerms)}</PdBadgeLabel>
                  </PdBadge>
                ) : (
                  <PdText>-</PdText>
                )}
              </View>
            </PdStack>

            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3]}
            >
              <View>
                <PdFormLabel>Available in Main Tab Credit</PdFormLabel>
                <View style={styles.pdInputGroup}>
                  <PdText style={styles.ml2}>$</PdText>
                  <PdHookFormInputFloat
                    name="availableMarketplaceCreditAmount"
                    style={[styles.flex1, styles.border0]}
                  />
                </View>
              </View>
              <View>
                <PdFormLabel>Available in Earned Credit</PdFormLabel>
                <PdText style={styles.mb3}>{formatMoney(data.retailer?.availableRewardsCreditAmount ?? 0)}</PdText>
              </View>
            </PdStack>

            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3]}
            >
              <View>
                <PdFormLabel>Available in Main Tab Credit</PdFormLabel>
                <PdText style={styles.mb3}>{formatMoney(data.retailer?.availableMarketplaceCreditAmount ?? 0)}</PdText>
              </View>
              <View>
                <PdFormLabel>Total Available to Retailer</PdFormLabel>
                <PdText style={styles.mb3}>
                  {formatMoney(
                    (data.retailer?.availableMarketplaceCreditAmount ?? 0) +
                      (data.retailer?.availableRewardsCreditAmount ?? 0),
                  )}
                </PdText>
              </View>
            </PdStack>

            <View style={styles.mb3}>
              <PdFormLabel>Balance Repayment Status</PdFormLabel>
              {query.data?.termsRepaymentStatus ? (
                <PdBadge
                  style={[
                    {
                      backgroundColor:
                        query.data.termsRepaymentStatus === "goodStanding" ? kColors.green200 : kColors.red200,
                    },
                    styles.mrAuto,
                  ]}
                >
                  <PdBadgeLabel>
                    {query.data.termsRepaymentStatus === "goodStanding" ? "Good Standing" : "Past Due"}
                  </PdBadgeLabel>
                </PdBadge>
              ) : (
                <PdText>-</PdText>
              )}
            </View>

            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3]}
            >
              <View>
                <PdFormLabel>Credit on Hold</PdFormLabel>
                <PdText style={styles.mb3}>{formatMoney(data.retailer?.creditOnHoldAmount ?? 0)}</PdText>
              </View>
              <View>
                <PdFormLabel>Vendor Bank Account Status</PdFormLabel>
                <PdBadge
                  style={[
                    {
                      backgroundColor: data.retailer.vendorId ? kColors.green200 : kColors.red200,
                    },
                    styles.mrAuto,
                  ]}
                >
                  <PdBadgeLabel>{data.retailer.vendorId ? "Connected" : "Not Connected"}</PdBadgeLabel>
                </PdBadge>
              </View>
              <View>
                <PdFormLabel>Release Credit?</PdFormLabel>
                <PdHookFormSwitch
                  name="shouldReleaseCredit"
                  disabled={!data.retailer?.creditOnHoldAmount || data.user.isDeactivated || !data.retailer.vendorId}
                  style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }}
                />
              </View>
            </PdStack>

            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3, !isMobile ? styles.alignEnd : {}]}
            >
              <View>
                <PdFormLabel>Total Brands Invites</PdFormLabel>
                <PdText>{query.isLoading ? "-" : query.data?.totalBrandInvites ?? 0}</PdText>
              </View>
              <View>
                <PdFormLabel>Total Reps Invites</PdFormLabel>
                <PdText>{query.isLoading ? "-" : query.data?.totalRepInvites ?? 0}</PdText>
              </View>
            </PdStack>

            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3, !isMobile ? styles.alignEnd : {}]}
            >
              <View>
                <PdFormLabel>Lifetime Main Tab Credit</PdFormLabel>
                <PdText>{formatMoney(data.retailer.lifetimeMarketplaceCreditAmount ?? 0)}</PdText>
              </View>
              <View>
                <PdFormLabel>Lifetime Earned Credit</PdFormLabel>
                <PdText>
                  {formatMoney(
                    (data.retailer.totalMarketplaceCreditAmount ?? 0) + (data.retailer.totalOrdersCreditAmount ?? 0),
                  )}
                </PdText>
              </View>
            </PdStack>

            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3, !isMobile ? styles.alignEnd : {}]}
            >
              <View>
                <PdFormLabel>Lifetime Main Tab Credit Redeemed</PdFormLabel>
                <PdText>{formatMoney(data.retailer.totalMarketplaceCreditRedeemedAmount ?? 0)}</PdText>
              </View>
              <View>
                <PdFormLabel>Lifetime Earned Credit Redeemed</PdFormLabel>
                <PdText>{formatMoney(data.retailer.totalEarnedCreditRedeemedAmount ?? 0)}</PdText>
              </View>
            </PdStack>

            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1]}
              gap={3}
              style={[styles.widthFull, styles.mb3, !isMobile ? styles.alignEnd : {}]}
            >
              <View>
                <PdFormLabel>Total Sales</PdFormLabel>
                <PdText>{query.isLoading ? "-" : formatMoney(query.data?.totalSales ?? 0)}</PdText>
              </View>
              <View />
            </PdStack>

            <View>
              <PdFormLabel>Notes</PdFormLabel>
              <PdHookFormInput
                name="internalNotes"
                multiline
                numberOfLines={8}
                placeholder="Internal Notes about Retailer"
              />
            </View>
          </View>
          {!isMobile && <PdDivider isVertical />}
          <View style={!isMobile ? styles.flex1 : {}}>
            <View style={styles.mb3}>
              <PdFormLabel>Primary Email</PdFormLabel>
              <PdText style={styles.mb3}>{data.user.email}</PdText>
              <PdButton
                onPress={handleOnPressChangeEmail}
                isDisabled={data.user.isDeactivated}
                size="sm"
                colorScheme="pink"
                style={styles.mrAuto}
              >
                <PdButtonLabel>Change Email</PdButtonLabel>
              </PdButton>
            </View>
            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3]}
            >
              <View>
                <PdFormLabel>Primary User Name</PdFormLabel>
                <PdHookFormInput name="userName" />
                <PdHookFormErrorMessage name="userName" />
              </View>
              <View>
                <PdFormLabel>User Role</PdFormLabel>
                <PdHookFormInput name="userTitle" />
                <PdHookFormErrorMessage name="userTitle" />
              </View>
            </PdStack>
            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1, 2]}
              gap={3}
              style={[styles.widthFull, styles.mb3]}
            >
              <View>
                <PdFormLabel>Account Manager</PdFormLabel>
                <PdHookFormInput name="accountManagerName" />
              </View>
              <View>
                <PdFormLabel>Internal Rank</PdFormLabel>
                <PdHookFormInput name="internalRank" />
              </View>
            </PdStack>
            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3, !isMobile ? styles.alignEnd : {}]}
            >
              <View>
                <PdFormLabel>Open Carts</PdFormLabel>
                <PdText>{query.isLoading ? "-" : formatMoney(query.data?.totalOpenCarts ?? 0)}</PdText>
              </View>
              <View>
                <PdFormLabel>Last Ordered</PdFormLabel>
                <PdText>
                  {data?.retailer.lastOrderPlacedDate
                    ? formatDate(data?.retailer.lastOrderPlacedDate.seconds * 1000)
                    : "-"}
                </PdText>
              </View>
            </PdStack>
            <View style={[styles.mb3, styles.zIndex3]}>
              <PdFormLabel>Primary Category</PdFormLabel>
              <PdHookFormSelect name="internalPrimaryCategory" options={orderedCategories} isClearable={false} />
              <PdHookFormErrorMessage name="internalPrimaryCategory" />
            </View>
            <View style={[styles.mb3, styles.zIndex2]}>
              <PdFormLabel>Secondary Category</PdFormLabel>
              <PdHookFormSelect name="internalSecondaryCategory" options={orderedCategories} isClearable={false} />
              <PdHookFormErrorMessage name="internalSecondaryCategory" />
            </View>
            <View style={[styles.mb3, styles.zIndex1]}>
              <PdFormLabel>Tertiary Category</PdFormLabel>
              <PdHookFormSelect name="internalTertiaryCategory" options={orderedCategories} isClearable={false} />
              <PdHookFormErrorMessage name="internalTertiaryCategory" />
            </View>
            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1]}
              gap={3}
              style={[styles.widthFull, styles.mb3, !isMobile ? styles.alignEnd : {}]}
            >
              <View>
                <PdFormLabel>Total Locations</PdFormLabel>
                <PdText>{data.retailer?.addresses.length ?? "-"}</PdText>
              </View>
              <View>
                <PdFormLabel>Reported Annual Sales</PdFormLabel>
                <PdText>
                  {query.isLoading || !data.retailer.annualSales ? "-" : formatMoney(data.retailer.annualSales)}
                </PdText>
              </View>
            </PdStack>
            <PdButton
              onPress={handleOnPressMessageRetailer}
              isDisabled={data.user.isDeactivated}
              size="sm"
              colorScheme="pink"
              style={styles.mrAuto}
            >
              <PdButtonLabel>Message Retailer</PdButtonLabel>
            </PdButton>
          </View>
        </PdStack>
      </View>

      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        gap={3}
        style={[styles.widthFull, styles.justifyBetween, styles.mt4]}
      >
        {data.user.isDeactivated ? (
          <PdButton
            isLoading={isActivatingOrDeactivating}
            onPress={handleOnPressActivate}
            variant="outline"
            colorScheme="red"
          >
            <PdButtonLabel>Activate Retailer</PdButtonLabel>
          </PdButton>
        ) : (
          <PdButton
            isLoading={isActivatingOrDeactivating}
            onPress={handleOnPressDeactivate}
            variant="outline"
            colorScheme="red"
          >
            <PdButtonLabel>Deactivate Retailer</PdButtonLabel>
          </PdButton>
        )}
        <PdButton isDisabled={data.user.isDeactivated} isLoading={isSubmitting} onPress={handleOnSubmit}>
          <PdButtonLabel>Save Changes</PdButtonLabel>
        </PdButton>
      </PdStack>
    </FormProvider>
  );
};

const P06_10_AdminRetailerSettings: React.FC = () => {
  const [retailerUser, setRetailerUser] = React.useState<tUser | undefined>(undefined);
  const [retailer, setRetailer] = React.useState<tBoutique | undefined>(undefined);
  const isFocused = useIsFocused();
  const route = useRoute<tAdminRetailerSettingsParams>();

  useFocusEffect(
    React.useCallback(() => {
      let userUnsubscribe: (() => void) | undefined;
      let retailerUnsubscribe: (() => void) | undefined;

      if (route?.params?.retailerId) {
        userUnsubscribe = queryUser({ companyRefPath: `boutiques/${route?.params?.retailerId}` }).onSnapshot(
          (snapshot) => {
            setRetailerUser(snapshot.docs[0]?.data());
          },
        );
        retailerUnsubscribe = documentRetailer(route?.params?.retailerId).onSnapshot((document) => {
          setRetailer(document.data());
        });
      }

      return () => {
        userUnsubscribe?.();
        retailerUnsubscribe?.();
      };
    }, [route?.params?.retailerId]),
  );

  React.useEffect(() => {
    if (!isFocused) {
      setRetailerUser(undefined);
      setRetailer(undefined);
    }
  }, [isFocused]);

  if (!retailerUser || !retailer) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return (
    <ScrollView style={[styles.p6, styles.bgLightGray]}>
      <AdminRetailerForm data={{ retailer, user: retailerUser }} />
    </ScrollView>
  );
};

export const P06_10 = React.memo(P06_10_AdminRetailerSettings);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  bgGray: {
    backgroundColor: kColors.backgroundGrey,
  },
  bgLightGray: {
    backgroundColor: kColors.lightGray,
  },
  bgWhite: {
    backgroundColor: kColors.white,
  },
  border0: {
    borderWidth: 0,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb3: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt1: {
    marginTop: 1 * kStyleConst.kSpacing,
  },
  mt4: {
    marginTop: 4 * kStyleConst.kSpacing,
  },
  p6: {
    padding: kStyleConst.kSpacing * 6,
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  pdSectionBadge: {
    alignItems: "center",
    backgroundColor: kColors.brandGreen,
    borderRadius: 999,
    height: 19,
    justifyContent: "center",
    width: 19,
  },
  pdSectionBadgeLabel: {
    ...kFonts.bodySmallCenter,
    color: kColors.white,
    lineHeight: 19,
  },
  px5: {
    paddingHorizontal: kStyleConst.kSpacing * 5,
  },
  py5: {
    paddingVertical: kStyleConst.kSpacing * 5,
  },
  roundFull: {
    borderRadius: 999,
  },
  sizeLg: {
    height: 141,
    width: 141,
  },
  widthFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
  zIndex2: {
    zIndex: 2,
  },
  zIndex3: {
    zIndex: 3,
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  sizeLg: {
    width: 141,
    height: 141,
  },
  roundFull: {
    borderRadius: 999,
  },
} as const;
